<template>
  <div class="bf-card bf-chart-card">
    <div class="bf-chart-card__head">
      <div class="bf-chart-card__title">Registros integrados no tempo</div>
      <div class="bf-chart-card__legend">
        <span class="bf-chart-card__legend-line"></span>
        Registros integrados
      </div>
    </div>

    <div class="chart yourProgressChart">
      <LineChart
        :width="300"
        :height="300"
        :data="chartData"
        :options="chartOptions"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed } from "vue";
import { Line } from "vue-chartjs";
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
} from "chart.js";
import type { DashboardResponse } from "@/types/response/dashboard";

ChartJS.register(
  Title,
  Tooltip,
  Legend,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale
);

export default defineComponent({
  name: "IntegratedLines",
  components: {
    LineChart: Line,
  },
  props: {
    data: {
      type: Object as () => DashboardResponse,
      required: true,
    },
  },
  setup(props) {
    const progressData = computed(() => {
      return Object.values(props.data.success_lines_per_day);
    });

    const categories = computed(() => {
      return Object.keys(props.data.success_lines_per_day).map((date) => {
        const [year, month, day] = date.split("-");
        return `${day}/${month}/${year}`;
      });
    });

    const chartData = computed(() => ({
      labels: categories.value,
      datasets: [
        {
          label: "Registros Integrados",
          data: progressData.value,
          backgroundColor: "rgba(155, 43, 255, 0.18)",
          borderColor: "#9b2bff",
          borderWidth: 2.5,
          pointBackgroundColor: "#9b2bff",
          pointBorderColor: "#9b2bff",
          pointRadius: 3,
          pointHoverRadius: 8,
          fill: true,
          tension: 0.4,
        },
      ],
    }));

    const chartOptions = computed(() => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false,
          position: "top" as const,
          labels: {
            color: "#9a93b8",
            font: {
              size: 9,
            },
          },
        },
        tooltip: {
          callbacks: {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            label: (context: any) => `${context.raw} registros integrados`,
          },
        },
      },
      scales: {
        y: {
          ticks: {
            color: "#9a93b8",
            font: {
              size: 9,
            },
          },
          grid: {
            color: "rgba(155, 43, 255, 0.08)",
            borderDash: [5, 5],
          },
        },
        x: {
          ticks: {
            color: "#9a93b8",
            font: {
              size: 11,
            },
          },
          grid: {
            display: false,
          },
        },
      },
    }));

    return {
      chartData,
      chartOptions,
    };
  },
});
</script>

<style lang="scss" scoped>
.bf-chart-card {
  padding: 20px 22px;

  &__head {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
  }

  &__title {
    font-size: 14px;
    font-weight: 700;
    color: var(--text);
  }

  &__legend {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 11.5px;
    color: var(--text3);
  }

  &__legend-line {
    width: 10px;
    height: 3px;
    border-radius: 2px;
    background: var(--brand2);
  }
}

.chart {
  position: relative;
  margin-top: 14px;
  height: 300px;
}
</style>
